import {
  Component,
  OnInit,
  ViewEncapsulation,
  NgModule,
  ViewChild,
  Output,
  EventEmitter,
  Input,
  HostBinding,
  HostListener,
  ElementRef,
  ChangeDetectorRef,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Images } from './interfaces';
import { Touches } from './touches';
import { Carousel } from './carousel';
import { Container } from './container';
import { Cells } from './cells';
import { Slide } from './slide';
import { Utils } from './utils';
import { Properties as CarouselProperties } from './interfaces';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { VgCoreModule } from '@videogular/ngx-videogular/core';
import { VgStreamingModule } from '@videogular/ngx-videogular/streaming';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { CarouselMediaCellComponent } from './carousel-media-cell/carousel-media-cell.component';
import { ImgWrapperComponent } from './media-wrappers/img-wrapper/img-wrapper.component';
import { PdfWrapperComponent } from './media-wrappers/pdf-wrapper/pdf-wrapper.component';
import { VideoWrapperComponent } from './media-wrappers/video-wrapper/video-wrapper.component';
import {OtherWrapperComponent} from "./media-wrappers/other-wrapper/other-wrapper.component";


@Component({
  selector: 'lib-carousel',
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.sass'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CarouselComponent implements OnInit {
  carousel: any;
  container: any;
  utils: any;
  cells: any;
  slide: any;
  _id!: string;
  _images!: Images;
  touches: any;
  landscapeMode: any;
  minTimeout = 30;
  isVideoPlaying = false;
  _isCounter = false;
  _width!: number;
  _cellWidth: number | '100%' = 200;
  _loop = false;
  _lightDOM = false;
  isMoving = false;
  isNgContent = false;
  videoLoading = true;
  cellLength!: number;
  dotsArr: any;
  carouselProperties!: CarouselProperties;
  savedCarouselWidth!: number;
  thumbnailsMap = new Map<string, { url: string; thumbnailUrl: string }>();
  _supportedVideos = ['avi', 'mov', 'mp4', 'wmv', 'm3u8', 'MOV', '3gp', 'mebm'];
  _supportedImages = ['png', 'jpg', 'jpeg'];
  @ViewChild('media') video!: ElementRef;

  //Sprint 77 - Photos Gallery Manager
  @Input() isGalleryManager!: boolean;
  @HostBinding('class.photos-manager') get galleryManager() {
    return this.isGalleryManager;
  }

  get isContainerLocked() {
    if (this.carousel) {
      return this.carousel.isContainerLocked;
    }
  }

  get slideCounter() {
    if (this.carousel) {
      return this.carousel.slideCounter;
    }
  }

  get lapCounter() {
    if (this.carousel) {
      return this.carousel.lapCounter;
    }
  }

  get isLandscape() {
    return window.innerWidth > window.innerHeight;
  }

  get isSafari(): any {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.indexOf('safari') !== -1) {
      return !(ua.indexOf('chrome') > -1);
    }
  }

  get counter() {
    let counter;

    if (this.loop) {
      counter = this.slideCounter % this.cellLength;
    } else {
      counter = this.slideCounter;
    }

    return counter + 1 + this.counterSeparator + this.cellLength;
  }

  get cellsElement() {
    return this.elementRef.nativeElement.querySelector('.carousel-cells');
  }

  get isArrows() {
    return this.isGalleryManager || (this.arrows && !this.freeScroll && this.images?.length > this.cellsToShow);
  }

  get isCounter() {
    return this._isCounter && this.cellLength > 1;
  }

  get activeDotIndex() {
    return this.slideCounter % this.cellLength;
  }

  get cellLimit() {
    if (this.carousel) {
      return this.carousel.cellLimit;
    }
  }

  get carouselWidth() {
    //console.log({w: this.elementRef.nativeElement.clientWidth});

    return this.elementRef.nativeElement.clientWidth;
  }

  @Output() events: EventEmitter<any> = new EventEmitter<any>();
  @Input() allowPrimaryCellsSelection = false;
  @Input() allowPrimaryTypes = [];
  @Input() allowSwitchableImagesPreview = false;
  @Input() id!: number;
  @Input() height = 200;
  @Input() width!: number;
  @Input() autoplay = false;
  @Input() autoplayInterval = 5000;
  @Input() pauseOnHover = true;
  @Input() dots = false;
  @Input() borderRadius!: number;
  @Input() margin = 10;
  @Input() objectFit: 'contain' | 'cover' | 'none' | 'fill' = 'cover';
  @Input() minSwipeDistance = 10;
  @Input() transitionDuration = 200;
  @Input() transitionTimingFunction: 'ease' | 'ease-in' | 'ease-out' | 'ease-in-out' | 'linear' = 'ease-out';
  @Input() videoProperties: any;
  @Input() counterSeparator = ' / ';
  @Input() overflowCellsLimit = 3;
  @Input() listeners: 'auto' | 'mouse and touch' = 'mouse and touch';
  @Input() cellsToShow!: number;
  @Input() cellsToScroll = 1;
  @Input() freeScroll = false;
  @Input() arrows = true;
  @Input() arrowsOutside = false;
  @Input() arrowsTheme: 'light' | 'dark' = 'light';
  @Input() allowDelete = false;
  @Input() allowHorizontalSwipe = false;
  // @ts-ignore
  @Input() thumbnail: string = null;
  // @ts-ignore
  @Input() rightArrow: number = null;
  // @ts-ignore
  @Input() leftArrow: number = null;

  @Input() maxCellWidth = 250;
  @Input() isOwner = false
  @Input()
  set images(images: Images & any) {
    this.processIncomingMediaFiles(images);
    let isSmallerList = false;
    if (this._images) {
      isSmallerList = images.length < Object.keys(this._images).length; //check if something changed in order to go to the beginning of the carousel
    }
    images.forEach((img: any) => {
      img.path = img?.path?.replace('http://', 'https://');
      img.fileType = img?.path?.split('.').at(-1);
    });

    this._images = images;
    //   console.log({ _images: this._images });

    if (this.carousel && isSmallerList && images.length != 1) {
      this.prev();
    }
  }
  get images() {
    return this._images;
  }
  preventClickItems = ['remove-element', 'stared', 'set-star', 'placeholder'];
  private processIncomingMediaFiles(images: Images & any) {
    images.forEach((img: any) => {
      img.path = img.path?.replace('http://', 'https://');
      const splitImgUrl: string[] = img.path.split('.');
      const fileType = splitImgUrl.pop()?.toLowerCase();
      if (fileType) {

        // switch (fileType) { //finding special types like movies and pdf
        //   case 'pdf':
        //     img['type'] = 'pdf'
        //     break;
        //   case 'mp4':
        //     img['type'] = 'video'
        //     break;
        //   default:
        //     img['type'] = 'image'
        //     break;
        // }
        if (this._supportedVideos.includes(fileType)) {
          img['type'] = 'video';
        } else if (this._supportedImages.includes(fileType)) {
          img['type'] = 'image';
        } else if (fileType == 'pdf') {
          img['type'] = 'pdf';
        } else {
          img['type'] = 'text'
        }
      }
    });
  }

  @Input('cellWidth') set cellWidth(value: number | '100%') {
    if (value) {
      this._cellWidth = value;
    }
  }

  @Input('counter') set isCounter(value: boolean) {
    if (value) {
      this._isCounter = value;
    }
  }

  @Input('loop') set loop(value: boolean) {
    if (value) {
      this._loop = value;
    }
  }

  get loop() {
    if (this.images) {
      return this._loop;
    } else {
      return false;
    }
  }

  @Input('lightDOM') set lightDOM(value: boolean) {
    if (value) {
      this._lightDOM = value;
    }
  }

  get lightDOM() {
    if (this.images) {
      return this._lightDOM;
    } else {
      return false;
    }
  }

  @HostBinding('class.carousel') hostClassCarousel: boolean = true;
  @HostBinding('style.height') hostStyleHeight!: string;
  @HostBinding('style.width') hostStyleWidth!: string;

  @HostListener('window:resize', ['$event'])
  onWindowResize(event: any) {
    if (this.utils.visibleWidth !== this.savedCarouselWidth) {
      this.resize();
    }
  }

  @HostListener('mousemove', ['$event'])
  onMousemove(event: MouseEvent) {
    if (this.autoplay && this.pauseOnHover) {
      this.carousel.stopAutoplay();
    }
  }

  @HostListener('mouseleave', ['$event'])
  onMouseleave(event: MouseEvent) {
    if (this.autoplay && this.pauseOnHover) {
      this.carousel.autoplay();
    }
  }

  constructor(private elementRef: ElementRef, public sanitizer: DomSanitizer, private ref: ChangeDetectorRef) { }

  ngOnInit() {
    this.isNgContent = this.cellsElement.children.length > 0;

    this.touches = new Touches({
      element: this.cellsElement,
      listeners: this.listeners,
      mouseListeners: {
        mousedown: 'handleMousedown',
        mouseup: 'handleMouseup',
      },
    });

    this.touches.on('touchstart', this.handleTouchstart);
    if (this.allowHorizontalSwipe) this.touches.on('horizontal-swipe', this.handleHorizontalSwipe);
    this.touches.on('touchend', this.handleTouchend);
    this.touches.on('mousedown', this.handleTouchstart);
    this.touches.on('mouseup', this.handleTouchend);
    this.touches.on('tap', this.handleTap);

    this.setDimensions();
  }

  ngAfterViewInit() {
    this.initCarousel();

    this.cellLength = this.getCellLength();
    this.dotsArr = Array(this.cellLength).fill(1);
    this.ref.detectChanges();
    this.carousel.lineUpCells();
    this.savedCarouselWidth = this.carouselWidth;
    this.events.emit({
      name: 'ready',
    });
    /* Start detecting changes in the DOM tree */
    this.detectDomChanges();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['width'] || changes['height'] || changes['images']) {
      this.setDimensions();
      this.initCarousel();
      this.carousel.lineUpCells();
      this.ref.detectChanges();
    }
  }

  ngOnDestroy() {
    this.touches.destroy();
  }

  initCarousel() {
    this.carouselProperties = {
      id: this.id,
      cellsElement: this.elementRef.nativeElement.querySelector('.carousel-cells'),
      hostElement: this.elementRef.nativeElement,
      images: this.images,
      cellWidth: this.getCellWidth(),
      loop: this.loop,
      autoplayInterval: this.autoplayInterval,
      overflowCellsLimit: this.overflowCellsLimit,
      visibleWidth: this.width,
      margin: this.margin,
      minSwipeDistance: this.minSwipeDistance,
      transitionDuration: this.transitionDuration,
      transitionTimingFunction: this.transitionTimingFunction,
      videoProperties: this.videoProperties,
      eventHandler: this.events,
      freeScroll: this.freeScroll,
      lightDOM: this.lightDOM,
    };

    this.utils = new Utils(this.carouselProperties);
    this.cells = new Cells(this.carouselProperties, this.utils);
    this.container = new Container(this.carouselProperties, this.utils, this.cells);
    this.slide = new Slide(this.carouselProperties, this.utils, this.cells, this.container);
    this.carousel = new Carousel(this.carouselProperties, this.utils, this.cells, this.container, this.slide);

    if (this.autoplay) {
      this.carousel.autoplay();
    }
  }

  resize() {
    this.landscapeMode = this.isLandscape;
    this.savedCarouselWidth = this.carouselWidth;

    this.carouselProperties.cellWidth = this.getCellWidth();
    this.cells.updateProperties(this.carouselProperties);
    this.carousel.updateProperties(this.carouselProperties);
    this.container.updateProperties(this.carouselProperties);
    this.slide.updateProperties(this.carouselProperties);
    this.utils.updateProperties(this.carouselProperties);
    this.carousel.lineUpCells();
    this.slide.select(0);
    this.ref.detectChanges();

    this.events.emit({
      name: 'resize',
    });
  }

  destroy() {
    this.carousel.destroy();
    this.utils = null;
    this.cells = null;
    this.container = null;
    this.slide = null;
    this.carousel = null;
  }

  detectDomChanges() {
    const observer = new MutationObserver(mutations => {
      this.onDomChanges();
    });

    var config = {
      attributes: true,
      childList: true,
      characterData: true,
    };
    observer.observe(this.cellsElement, config);
  }

  onDomChanges() {
    this.cellLength = this.getCellLength();
    this.carousel.lineUpCells();
    this.ref.detectChanges();
  }

  setDimensions() {
    this.hostStyleHeight = this.height + 'px';
    this.hostStyleWidth = this.width + 'px';
  }

  getImage(index: number) {
    return this.carousel.getImage(index);
  }

  handleTouchstart = (event: any) => {
    //event.preventDefault();
    this.touches.addEventListeners('mousemove', 'handleMousemove');
    this.carousel.handleTouchstart(event);
    this.isMoving = true;
    this.events.emit({
      name: 'touchstart',
      event,
    });
  };

  handleHorizontalSwipe = (event: any) => {
    event.preventDefault();
    this.carousel.handleHorizontalSwipe(event);
    this.events.emit({
      name: 'move',
      event,
    });
  };

  handleTouchend = (event: any) => {
    const touches = event.touches;
    this.carousel.handleTouchend(event);
    this.touches.removeEventListeners('mousemove', 'handleMousemove');
    this.isMoving = false;
    this.events.emit({
      name: 'touchend',
      event,
    });
  };

  handleTap = (event: any) => {
    if (!this.preventClickItems.includes(event?.target?.id)) {
      const outboundEvent: any = {
        name: 'click',
      };
      const nodes = Array.prototype.slice.call(this.cellsElement.children);
      const cellElement = event.srcElement.closest('.carousel-cell');
      const i = nodes.indexOf(cellElement);
      const cellIndex = nodes.indexOf(cellElement);

      if (this.images) {
        const imageIndex = this.cells.getImageIndex(i);
        const file = this.cells.getImage(imageIndex);
        outboundEvent.image = file.image;
        outboundEvent.imageIndex = file.imageIndex;
        if (this.allowSwitchableImagesPreview) {
          const images = this.cells.getImages();
          outboundEvent.images = images;
        }
        if (this.isGalleryManager) {
          outboundEvent.images = this.images.filter((image: { path: string; }) => image.path !== 'placeholder');
          outboundEvent.imageIndex = file.imageIndex;
        }
      } else {
        outboundEvent.cellIndex = cellIndex;
      }

      this.events.emit(outboundEvent);
    }
  };

  handleTransitionendCellContainer(event: any) {
    if (event.target['className'] === 'carousel-cells') {
      this.carousel.handleTransitionend();
    }

    let direction;
    if (this.slide.direction === 'left') {
      direction = 'next';
    }
    if (this.slide.direction === 'right') {
      direction = 'prev';
    }

    this.events.emit({
      name: direction,
      counter: this.slide.counter,
    });

    this.events.emit({
      name: 'transitionend',
    });
  }

  getCellWidth() {
    const elementWidth = this.carouselWidth;
    // console.log({elementWidth});

    if (this.cellsToShow) {
      const margin = this.cellsToShow > 1 ? this.margin : 0;
      const totalMargin = margin * (this.cellsToShow - 1);
      return (elementWidth - totalMargin) / this.cellsToShow > this.maxCellWidth
        ? this.maxCellWidth
        : (elementWidth - totalMargin) / this.cellsToShow;
    }

    if (this._cellWidth === '100%') {
      return elementWidth > this.maxCellWidth ? this.maxCellWidth : elementWidth;
    } else {
      return this._cellWidth > this.maxCellWidth ? this.maxCellWidth : this._cellWidth;
    }
  }

  next() {
    this.carousel.next(this.cellsToScroll);
    this.carousel.stopAutoplay();
  }

  prev() {
    this.carousel.prev(this.cellsToScroll);
    this.carousel.stopAutoplay();
  }

  select(index: number) {
    this.carousel.select(index);
  }

  play() {
    this.carousel.autoplay();
  }

  stop() {
    this.carousel.stopAutoplay();
  }

  isNextArrowDisabled() {
    if (this.carousel) {
      return this.carousel.isNextArrowDisabled();
    }
  }

  isPrevArrowDisabled() {
    if (this.carousel) {
      return this.carousel.isPrevArrowDisabled();
    }
  }

  getCellLength() {
    if (this.images) {
      return this.images.length;
    } else {
      return this.cellsElement.children.length;
    }
  }

  handleMediaCellAction(event: any) {
    switch (event.action) {
      case 'remove':
        {
          this.events.emit({
            name: 'delete',
            key: event.value.src,
          });
        }
        break;
      case 'set-primary': {
        this.events.emit({
          name: 'set-primary',
          key: event.value.src,
        });
      }
    }
  }
}

@NgModule({
  imports: [
    CommonModule,
    CommonModule,
    PdfViewerModule,
    VgStreamingModule,
    VgCoreModule,
    TranslateModule,
    MatTooltipModule,
  ],
  declarations: [
    CarouselComponent,
    CarouselMediaCellComponent,
    ImgWrapperComponent,
    PdfWrapperComponent,
    VideoWrapperComponent,
    OtherWrapperComponent
  ],
  exports: [
    CarouselComponent
  ],
})
export class CarouselComponentModule { }
